import ShareButton from 'components/buttons/Share';
import truncate from 'lodash/truncate';
import PropTypes from 'prop-types';
import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import strip from 'strip';
import { useAuthContext } from 'AuthContext';
import LikeButton from 'components/buttons/Like';
import Card from 'components/Card';
import CardActions from 'components/Card/Actions';
import CardChips from 'components/Card/Chips';
import CardHeader from 'components/Card/Header';
import CardText from 'components/Card/Text';
import CardSticker from 'components/CardSticker';
import HTMLFragment from 'components/HTMLFragment';
import HeroCarousel from 'containers/HeroCarousel';
import { getEventPartyDate, getEventHashtags } from 'lib/eventUtils';
import stopEvent from 'lib/stopEvent';
import useIsMobile from 'lib/useIsMobile';
import { useApolloClient } from '@apollo/client';
import { GET_EVENT_SEARCH } from '../../../schema/apolloQueries/communityDetailsQueries';

/* eslint-disable */

const EventCard = ({
  compact,
  event,
  viewer,
  handleLike,
  data,
  setEventData,
  onClickDiv,
  ...props
}) => {
  const { handleIntercepted, interceptAction } = useAuthContext();
  const mobile = useIsMobile();
  const [liking, setLiking] = useState(false);

  const client = useApolloClient();

  const updateLikedStatus = (eventId, newLikedStatus, count) => {
    const eventIndex = data.getSponsorEvents.data.findIndex(
      list => list.id === eventId
    );
    if (eventIndex !== -1) {
      const updatedEvents = [...data.getSponsorEvents.data];
      updatedEvents[eventIndex] = {
        ...updatedEvents[eventIndex],
        liked: newLikedStatus,
        likeCount: count,
      };
      client.writeQuery({
        query: GET_EVENT_SEARCH,
        data: {
          fetchSponsorDetails: data.fetchSponsorDetails,
          getSponsorEvents: {
            data: updatedEvents,
            total: data.getSponsorEvents.total,
            __typename: data.getSponsorEvents.__typename,
          },
        },
      });
      setEventData({
        fetchSponsorDetails: data.fetchSponsorDetails,
        getSponsorEvents: {
          data: updatedEvents,
          total: data.getSponsorEvents.total,
          __typename: data.getSponsorEvents.__typename,
        },
      });
    } else {
      client.writeQuery({
        query: GET_EVENT_SEARCH,
        data,
      });
      setEventData(data);
    }
  };

  const showHeader = useMemo(() => compact || (!compact && !mobile), [
    compact,
    mobile,
  ]);

  const sticker = useMemo(() => {
    const { backgroundColor, label, labelStyle } = {} || {};
    return backgroundColor && label ? (
      <CardSticker
        backgroundColor={backgroundColor}
        label={label}
        labelStyle={labelStyle}
      />
    ) : null;
  }, [event]);

  const like = useCallback(() => {
    setLiking(true);
    handleLike({
      variables: {
        isLiked: !event.liked,
        id: event?.id,
      },
    }).then(likeRes => {
      if (likeRes?.data?.likeOrDislikeSponsor === true) {
        updateLikedStatus(event?.id, true, event.likeCount + 1);
      } else {
        updateLikedStatus(event?.id, false, event.likeCount - 1);
      }
      setLiking(false);
    });
  }, [event, data]);

  const onLike = useCallback(
    e => {
      stopEvent(e);
      if (viewer?.loggedIn) {
        like();
      } else {
        interceptAction(`like-${event.id}`);
      }
    },
    [event.id, interceptAction, like, viewer?.loggedIn]
  );

  const actions = useMemo(
    () => (
      <CardActions noTopPadding>
        <LikeButton
          disabled={liking}
          liked={event.liked}
          likes={event.likeCount}
          onClick={onLike}
          primary={!liking}
        />
        {!compact && mobile && (
          <ShareButton
            hashtags={getEventHashtags(event)}
            primary
            title={`${strip(event.community.name)} ${strip(event.name)}`}
            iconStyle={{
              transform: 'scaleX(-1)',
            }}
          />
        )}
      </CardActions>
    ),
    [event, compact, liking, mobile, onLike, data]
  );

  const content = useMemo(() => {
    if (compact) {
      if (event.summary) {
        return event.summary;
      }
      return truncate(event.description, {
        length: 140,
        separator: ' ',
      });
    }
    return event.description;
  }, [compact, event.description, event.summary]);

  const body = useMemo(() => {
    const partyDate = getEventPartyDate(event);
    return (
      <Fragment>
        <div onClick={onClickDiv}>
          <HeroCarousel playlist={event.heroPlayList} compact={compact} />
        </div>
        {!compact && mobile && actions}
        {showHeader && (
          <div onClick={onClickDiv}>
            <CardHeader
              title={
                <HTMLFragment style={{ fontSize: 15, fontWeight: '700' }}>
                  {`${event.community.name} ${event.name}`}
                </HTMLFragment>
              }
            />
          </div>
        )}
        {partyDate && (
          <div onClick={onClickDiv}>
            <CardText noTopPadding>
              <em>{partyDate}</em>
            </CardText>
          </div>
        )}
        <div onClick={onClickDiv}>
          <CardText noTopPadding>
            <HTMLFragment>{content}</HTMLFragment>
          </CardText>
        </div>
        {!compact && event.categories.length > 0 && (
          <div onClick={onClickDiv}>
            <CardChips
              items={event.categories.map(category => category.name)}
              noTopPadding
            />
          </div>
        )}
        {compact && (
          <Fragment>
            {actions}
            {sticker}
          </Fragment>
        )}
      </Fragment>
    );
  }, [actions, compact, content, event, mobile, showHeader, sticker]);

  useEffect(() => {
    if (viewer?.loggedIn) {
      handleIntercepted(`like-${event.id}`, like);
    }
  }, [event.id, handleIntercepted, like, viewer?.loggedIn]);
  return (
    <div style={{ marginBottom: '1rem' }}>
      <Card {...props}>{body}</Card>
    </div>
  );
};

EventCard.propTypes = {
  compact: PropTypes.bool,
  event: PropTypes.object.isRequired,
  viewer: PropTypes.object.isRequired,
  handleLike: PropTypes.func.isRequired,
  data: PropTypes.any,
  setEventData: PropTypes.any,
  onClickDiv: PropTypes.any,
};

export default EventCard;
